/**
 * @file apps/web/src/pages/DashboardPage.tsx
 * @description PM dashboard overview. Fetches cross-project stats and renders PMDashboard.
 * Aggregates pass rates, defect counts, and test script distribution from the API.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, Plus } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { projectsApi, testScriptsApi, uatApi, approvalsApi } from '../lib/api.js';
import { PMDashboard, type PMStats, type PassRatePoint, type DefectSeverityPoint } from '../components/PMDashboard.js';

// ---------------------------------------------------------------------------
// Types (loosely typed from API unknown return)
// ---------------------------------------------------------------------------

interface Project {
  id:        string;
  name:      string;
  status:    string;
  client_id: string;
}

interface TestScript {
  id:     string;
  status: string;
}

interface UatSession {
  id:     string;
  name:   string;
  status: string;
}

interface Defect {
  id:       string;
  status:   string;
  severity: string;
}

// ---------------------------------------------------------------------------
// DashboardPage
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const { user, devHeaders } = useAuth();

  const [projects, setProjects]   = useState<Project[]>([]);
  const [stats, setStats]         = useState<PMStats | null>(null);
  const [passRateData, setPassRateData] = useState<PassRatePoint[]>([]);
  const [defectData, setDefectData]     = useState<DefectSeverityPoint[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // Fetch projects for this tenant, and this user's pending-approval
        // inbox (GET /approvals/inbox — scoped server-side to request.userId
        // as approver, same source ApprovalsInboxPage uses) in parallel.
        const [projs, inbox] = await Promise.all([
          projectsApi.list(user.tenantId, devHeaders) as Promise<Project[]>,
          approvalsApi.inbox(devHeaders),
        ]);
        if (cancelled) return;
        setProjects(projs);

        // Aggregate stats across all projects
        const activeProjects = projs.filter((p) => p.status === 'active').length;

        // For each project, fetch scripts + sessions + defects in parallel
        const perProjectData = await Promise.all(
          projs.map(async (p) => {
            const [scripts, sessions, defects] = await Promise.all([
              testScriptsApi.list(p.id, devHeaders) as Promise<TestScript[]>,
              uatApi.listSessions(p.id, devHeaders) as Promise<UatSession[]>,
              uatApi.listDefects(p.id, devHeaders) as Promise<Defect[]>,
            ]);
            return { project: p, scripts, sessions, defects };
          })
        );
        if (cancelled) return;

        // Aggregate
        let totalScripts = 0, passed = 0, failed = 0, draft = 0;
        let openUat = 0, openDefects = 0;
        const defectsBySeverity: Record<string, number> = {};

        for (const { scripts, sessions, defects } of perProjectData) {
          totalScripts += scripts.length;
          passed       += scripts.filter((s) => s.status === 'passed').length;
          failed       += scripts.filter((s) => s.status === 'failed').length;
          draft        += scripts.filter((s) => s.status === 'draft').length;

          openUat += sessions.filter((s) =>
            s.status === 'in_progress' || s.status === 'scheduled'
          ).length;

          const open = defects.filter((d) =>
            d.status !== 'resolved' && d.status !== 'closed'
          );
          openDefects += open.length;

          for (const d of open) {
            defectsBySeverity[d.severity] = (defectsBySeverity[d.severity] ?? 0) + 1;
          }
        }

        const passRate = totalScripts > 0 ? Math.round((passed / totalScripts) * 100) : 0;

        const computedStats: PMStats = {
          activeProjects,
          openUatSessions:   openUat,
          pendingApprovals:  inbox.length,
          openDefects,
          passRate,
          testScriptsTotal:  totalScripts,
          testScriptsPassed: passed,
          testScriptsFailed: failed,
          testScriptsDraft:  draft,
        };

        // Pass-rate trend: one point per project (simplified — use project name as label)
        const trend: PassRatePoint[] = perProjectData
          .filter(({ scripts }) => scripts.length > 0)
          .map(({ project, scripts }) => {
            const p = scripts.filter((s) => s.status === 'passed').length;
            const f = scripts.filter((s) => s.status === 'failed').length;
            const total = scripts.length;
            return {
              label:   project.name.slice(0, 12),
              pass:    total > 0 ? Math.round((p / total) * 100) : 0,
              fail:    total > 0 ? Math.round((f / total) * 100) : 0,
              blocked: 0,
            };
          });

        // Defect severity breakdown
        const severity: DefectSeverityPoint[] = Object.entries(defectsBySeverity).map(
          ([severity, count]) => ({ severity, count })
        );

        setStats(computedStats);
        setPassRateData(trend);
        setDefectData(severity);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-8 max-w-6xl">
      {/* Page header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-sm text-muted mt-1">
            Welcome back, {user.fullName}
          </p>
        </div>
        <Link to="/projects" className="btn-primary text-sm">
          <FolderOpen size={15} /> View Projects
        </Link>
      </div>

      {error && (
        <div className="p-4 bg-danger/8 rounded-lg text-danger text-sm mb-6">
          {error}
        </div>
      )}

      {/* PMDashboard */}
      {stats ? (
        <PMDashboard
          stats={stats}
          passRateData={passRateData}
          defectData={defectData}
          loading={loading}
        />
      ) : (
        <PMDashboard
          stats={{
            activeProjects: 0, openUatSessions: 0, pendingApprovals: 0, openDefects: 0,
            passRate: 0, testScriptsTotal: 0, testScriptsPassed: 0, testScriptsFailed: 0, testScriptsDraft: 0,
          }}
          passRateData={[]}
          defectData={[]}
          loading={loading}
        />
      )}

      {/* Projects quick list */}
      {!loading && projects.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-sm font-semibold text-ink mb-3">Your Projects</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="card p-4 hover:ring-1 hover:ring-blue/30 transition-all group"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-ink group-hover:text-blue transition-colors leading-snug">
                    {p.name}
                  </p>
                  <span className={[
                    'text-[10px] font-mono px-1.5 py-0.5 rounded-full shrink-0',
                    p.status === 'active'    ? 'bg-success/12 text-success' :
                    p.status === 'completed' ? 'bg-sky text-blue' :
                    'bg-gray text-muted',
                  ].join(' ')}>
                    {p.status}
                  </span>
                </div>
                <p className="text-[11px] font-mono text-muted mt-1 truncate">{p.id.slice(0, 8)}…</p>
              </Link>
            ))}

            <Link
              to="/projects"
              className="card p-4 border-2 border-dashed border-[rgba(8,17,31,0.1)] flex items-center justify-center gap-2 text-sm text-muted hover:text-blue hover:border-blue/40 transition-colors"
            >
              <Plus size={14} /> New Project
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
